import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/i18n/LanguageContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { useSubscription, useAuth } from '@/utils/AuthContext';
import { paymentsApi, promoCodesApi, subscriptionApi, type SubscriptionStatus } from '@/utils/api-pb';
import { getPhRef } from '@/utils/phRef';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ServiceFormModal } from '@/components/subscription/ServiceFormModal';
import { API_URL } from '@/utils/config';
import { pb } from '@/utils/pocketbase';

type PlanFeatureKey = string;

interface PlanDef {
  id: string;
  nameKey: string;
  emojiKey: string;
  price: number;
  popular?: boolean;
  isService?: boolean;
  isRecurring?: boolean;
  featureKeys: PlanFeatureKey[];
  blockedFeatureKeys?: PlanFeatureKey[];
}

// Consent text version — update when wording changes
const CONSENT_TEXT_VERSION = 'Согласие на ежемесячные автоматические списания. Версия 2026-06-28.';
const ROBOKASSA_SCRIPT_ID = 'robokassa-iframe-script';
const ROBOKASSA_SCRIPT_SRC = 'https://auth.robokassa.ru/Merchant/bundle/robokassa_iframe.js';

let robokassaScriptPromise: Promise<RobokassaStatic> | null = null;

function loadRobokassa(): Promise<RobokassaStatic> {
  if (typeof Robokassa !== 'undefined') {
    return Promise.resolve(Robokassa);
  }

  if (robokassaScriptPromise) {
    return robokassaScriptPromise;
  }

  robokassaScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(ROBOKASSA_SCRIPT_ID) as HTMLScriptElement | null;

    const resolveIfReady = () => {
      if (typeof Robokassa !== 'undefined') {
        resolve(Robokassa);
      } else {
        reject(new Error('Robokassa script loaded without global API'));
      }
    };

    if (existingScript) {
      existingScript.addEventListener('load', resolveIfReady, { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Robokassa script')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = ROBOKASSA_SCRIPT_ID;
    script.type = 'text/javascript';
    script.async = true;
    script.src = ROBOKASSA_SCRIPT_SRC;
    script.addEventListener('load', resolveIfReady, { once: true });
    script.addEventListener('error', () => reject(new Error('Failed to load Robokassa script')), { once: true });
    document.head.appendChild(script);
  });

  return robokassaScriptPromise;
}

// RUB prices for Russian users (Basic/Full — monthly recurring)
const plansRu: PlanDef[] = [
  {
    id: 'free',
    nameKey: 'plans.free.name',
    emojiKey: 'plans.free.emoji',
    price: 0,
    featureKeys: [
      'features.1project',
      'features.10messages',
      'features.marketAnalysis',
      'features.persona',
    ],
    blockedFeatureKeys: [
      'features.researchChat',
      'features.positioning',
      'features.businessModel',
      'features.marketingStrategy',
      'features.mvpPlan',
      'features.financialModel',
    ],
  },
  {
    id: 'basic',
    nameKey: 'plans.basic.name',
    emojiKey: 'plans.basic.emoji',
    price: 490,
    isRecurring: true,
    featureKeys: [
      'features.1project',
      'features.50messages',
      'features.marketAnalysis',
      'features.persona',
      'features.researchChat',
      'features.positioning',
      'features.businessModel',
      'features.marketingStrategy',
      'features.mvpPlan',
      'features.financialModel',
    ],
  },
  {
    id: 'full',
    nameKey: 'plans.full.name',
    emojiKey: 'plans.full.emoji',
    price: 890,
    popular: true,
    isRecurring: true,
    featureKeys: [
      'features.5projects',
      'features.100messages',
      'features.marketAnalysis',
      'features.persona',
      'features.researchChat',
      'features.positioning',
      'features.businessModel',
      'features.marketingStrategy',
      'features.mvpPlan',
      'features.financialModel',
    ],
  },
  {
    id: 'mvp_turnkey',
    nameKey: 'plans.mvpTurnkey.name',
    emojiKey: 'plans.mvpTurnkey.emoji',
    price: 100000,
    isService: true,
    featureKeys: [
      'features.turnkey.analytics',
      'features.turnkey.product',
      'features.turnkey.botSitePlatform',
      'features.turnkey.support',
      'features.turnkey.details',
    ],
  },
];

// USD prices for English users
const plansEn: PlanDef[] = [
  {
    id: 'free',
    nameKey: 'plans.free.name',
    emojiKey: 'plans.free.emoji',
    price: 0,
    featureKeys: [
      'features.1project',
      'features.10messages',
      'features.marketAnalysis',
      'features.persona',
    ],
    blockedFeatureKeys: [
      'features.researchChat',
      'features.positioning',
      'features.businessModel',
      'features.marketingStrategy',
      'features.mvpPlan',
      'features.financialModel',
    ],
  },
  {
    id: 'basic',
    nameKey: 'plans.basic.name',
    emojiKey: 'plans.basic.emoji',
    price: 15,
    featureKeys: [
      'features.1project',
      'features.50messages',
      'features.marketAnalysis',
      'features.persona',
      'features.researchChat',
      'features.positioning',
      'features.businessModel',
      'features.marketingStrategy',
      'features.mvpPlan',
      'features.financialModel',
    ],
  },
  {
    id: 'full',
    nameKey: 'plans.full.name',
    emojiKey: 'plans.full.emoji',
    price: 19,
    popular: true,
    featureKeys: [
      'features.5projects',
      'features.100messages',
      'features.marketAnalysis',
      'features.persona',
      'features.researchChat',
      'features.positioning',
      'features.businessModel',
      'features.marketingStrategy',
      'features.mvpPlan',
      'features.financialModel',
    ],
  },
  {
    id: 'mvp_turnkey',
    nameKey: 'plans.mvpTurnkey.name',
    emojiKey: 'plans.mvpTurnkey.emoji',
    price: 1490,
    isService: true,
    featureKeys: [
      'features.turnkey.analytics',
      'features.turnkey.product',
      'features.turnkey.botSitePlatform',
      'features.turnkey.support',
      'features.turnkey.details',
    ],
  },
];

type ModalType = 'changePlan' | 'success' | 'error' | 'cancelConfirm' | null;

export default function SubscriptionPage() {
  const { tier, isPremium, endDate, daysRemaining } = useSubscription();
  const { refreshUser } = useAuth();
  const { t } = useTranslation('subscription');
  const { lang } = useLanguage();
  const plans = lang === 'en' ? plansEn : plansRu;
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [modalType, setModalType] = useState<ModalType>(null);
  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoSuccess, setPromoSuccess] = useState<string | null>(null);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  // Consent state per plan (basic/full)
  const [consentChecked, setConsentChecked] = useState<Record<string, boolean>>({
    basic: false,
    full: false,
  });
  // Subscription status from backend (recurring details)
  const [subStatus, setSubStatus] = useState<SubscriptionStatus | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelSuccessMsg, setCancelSuccessMsg] = useState<string | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, []);

  // Проверяем URL параметры и путь для показа модалок
  useEffect(() => {
    const status = searchParams.get('status');
    const isSuccessRoute = location.pathname.endsWith('/payment/success');
    const isFailRoute = location.pathname.endsWith('/payment/fail');

    if (status === 'success' || isSuccessRoute) {
      refreshUser().then(() => {
        setModalType('success');
      });
    } else if (status === 'fail' || isFailRoute) {
      setModalType('error');
    }
  }, [searchParams, location.pathname, refreshUser]);

  // Load subscription status for paid users
  useEffect(() => {
    if (!isPremium) {
      setSubStatus(null);
      return;
    }
    let cancelled = false;
    subscriptionApi
      .getStatus()
      .then((status) => {
        if (!cancelled) setSubStatus(status);
      })
      .catch((err) => {
        console.error('Failed to load subscription status:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [isPremium, tier]);

  const handleSubscribe = async (planId: string) => {
    if (planId === 'free') return;

    // Если пользователь уже на платном тарифе и хочет сменить на другой
    if (isPremium && planId !== tier) {
      setPendingPlanId(planId);
      setModalType('changePlan');
      return;
    }

    await processPayment(planId);
  };

  const processPayment = async (planId: string) => {
    try {
      setIsLoading(planId);
      setModalType(null);

      // EN users — crypto payment (NOWPayments new tab)
      if (lang === 'en') {
        const response = await fetch(`${API_URL}/api/crypto-payment/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${pb.authStore.token}`,
          },
          body: (() => {
            // localStorage first, shared .create-products.com cookie as fallback
            const phRef = getPhRef();
            return JSON.stringify({
              planType: planId,
              lang,
              ...(phRef ? { ph_ref: phRef } : {}),
            });
          })(),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Payment error');
        }

        if (result.invoice_url) {
          window.open(result.invoice_url, '_blank');
        } else {
          throw new Error('No payment page returned');
        }

        setIsLoading(null);
        return;
      }

      // RU users — Robokassa payment flow
      const hasPromo = !!promoCode.trim();
      const isRecurringPlan = planId === 'basic' || planId === 'full';

      const result = await paymentsApi.createPayment({
        subscriptionType: planId as 'basic' | 'full',
        ...(hasPromo ? { promoCode: promoCode.trim() } : {}),
        ...(isRecurringPlan
          ? {
              recurring: true,
              consentGiven: true,
              consentText: CONSENT_TEXT_VERSION,
            }
          : {}),
      });

      if (result.iframeParams) {
        const robokassa = await loadRobokassa();
        robokassa.StartPayment(result.iframeParams);
        setIsLoading(null);

        // Start polling for payment completion
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);

        pollIntervalRef.current = setInterval(async () => {
          try {
            const status = await paymentsApi.checkPaymentStatus(result.invoiceId);
            if (status.status === 'success') {
              if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
              if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
              robokassa.ClosePaymentForm();
              await refreshUser();
              setModalType('success');
            } else if (status.status === 'failed') {
              if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
              if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
              robokassa.ClosePaymentForm();
              setModalType('error');
            }
          } catch {
            // Ignore poll errors, keep trying
          }
        }, 3000);

        // Stop polling after 10 minutes (timeout)
        pollTimeoutRef.current = setTimeout(() => {
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        }, 10 * 60 * 1000);
      } else if (result.paymentUrl) {
        window.location.href = result.paymentUrl;
      }
    } catch (err: any) {
      console.error('Payment error:', err);
      setModalType('error');
      setIsLoading(null);
    }
  };

  const confirmChangePlan = async () => {
    if (pendingPlanId) {
      await processPayment(pendingPlanId);
      setPendingPlanId(null);
    }
  };

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) return;

    try {
      setPromoLoading(true);
      setPromoError(null);
      setPromoSuccess(null);

      const result = await promoCodesApi.apply(promoCode.trim());
      setPromoSuccess(result.message || t('promo.successDefault'));
      setPromoCode('');
    } catch (err: any) {
      setPromoError(err.message || t('promo.errorDefault'));
    } finally {
      setPromoLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setCancelLoading(true);
      setCancelError(null);
      await subscriptionApi.cancel();
      await refreshUser();
      const fresh = await subscriptionApi.getStatus();
      setSubStatus(fresh);
      const endDateStr = fresh.endDate
        ? new Date(fresh.endDate).toLocaleDateString(lang === 'en' ? 'en-US' : 'ru-RU')
        : '';
      setCancelSuccessMsg(t('cancel.success', { date: endDateStr }));
      setModalType(null);
    } catch (err: any) {
      console.error('Cancel error:', err);
      setCancelError(err.message || t('cancel.error'));
    } finally {
      setCancelLoading(false);
    }
  };

  // Определяем, является ли текущий тариф легаси-подпиской
  const isLegacyTier = tier === 'creator' || tier === 'top_creator';
  // Determine if user has new recurring (basic/full) subscription
  const isRecurringTier = tier === 'basic' || tier === 'full';
  const isLegacyOneTime = !!subStatus?.isLegacyOneTime;

  const formatDate = (date?: string | null): string =>
    date ? new Date(date).toLocaleDateString(lang === 'en' ? 'en-US' : 'ru-RU') : '';

  // RU locale only — show consent + monthly billing UI for basic/full
  const showRecurringUi = lang !== 'en';

  return (
    <><PageHeader title={t('pageTitle')} />
      {/* Подзаголовок */}
      <div className="mb-[24px]">
        <p className="font-segoe text-[16px] leading-[24px] text-[color:var(--grey-text,#565858)] max-w-[650px]">
          {t('subtitle')}
        </p>
      </div>

      {/* Карточка ошибки оплаты */}
      {(searchParams.get('status') === 'fail' || location.pathname === '/payment/fail') && (
        <div className="mb-[24px] rounded-[16px] border border-[var(--error-outline,#ff9196)] p-[24px]">
          <div className="mb-[16px]">
            <h3 className="font-manrope font-semibold text-[20px] leading-[28px] text-[color:var(--black-text,#0d0d0d)]">{t('paymentError.title')}</h3>
            <p className="mt-[8px] font-segoe text-[16px] leading-[24px] text-[color:var(--grey-text,#565858)]">
              {t('paymentError.description')}
            </p>
          </div>
        </div>
      )}

      {/* Легаси-подписка — информация (creator/top_creator) */}
      {isLegacyTier && (
        <div className="mb-[24px] rounded-[16px] border border-[var(--main-brand,#2197f1)] bg-[var(--accent-background,rgba(33,151,241,0.1))] p-[24px]">
          <p className="font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)]">
            {t('legacy.activePlan')}{' '}
            <strong>{tier === 'creator' ? t('legacy.creator') : t('legacy.topCreator')}</strong>
            {endDate && (<> {t('legacy.until', { date: new Date(endDate).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) })}</>)}.{' '}
            {t('legacy.switchInfo')}
          </p>
        </div>
      )}

      {/* Блок управления подпиской basic/full */}
      {isRecurringTier && subStatus && (
        <div className="mb-[24px] rounded-[16px] border border-[var(--grey-accent,#eeeeee)] bg-[var(--white,#ffffff)] p-[24px]">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-[16px]">
            <div className="flex flex-col gap-[4px]">
              {isLegacyOneTime ? (
                <p className="font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)]">
                  {t('buttons.expiresOn', { date: formatDate(subStatus.endDate) })}
                </p>
              ) : subStatus.isCancelled ? (
                <p className="font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)]">
                  {t('status.cancelledUntil')}{' '}
                  <strong>{formatDate(subStatus.endDate)}</strong>
                </p>
              ) : (
                <>
                  <p className="font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)]">
                    {t('status.activeUntil')}{' '}
                    <strong>{formatDate(subStatus.endDate)}</strong>
                  </p>
                  {subStatus.nextBillingDate && (
                    <p className="font-segoe text-[14px] leading-[20px] text-[color:var(--grey-text,#565858)]">
                      {t('status.nextBilling')}: {formatDate(subStatus.nextBillingDate)}
                    </p>
                  )}
                </>
              )}
              {cancelSuccessMsg && (
                <p className="mt-[4px] font-segoe text-[14px] leading-[20px] text-[color:var(--main-brand,#2197f1)]">
                  {cancelSuccessMsg}
                </p>
              )}
            </div>
            {!isLegacyOneTime && !subStatus.isCancelled && (
              <Button
                variant="outline"
                className="h-[40px] rounded-[16px] border-[var(--grey-accent,#eeeeee)] font-segoe font-semibold text-[14px] leading-[20px] text-[color:var(--black-text,#0d0d0d)]"
                onClick={() => {
                  setCancelError(null);
                  setModalType('cancelConfirm');
                }}
              >
                {t('cancel.button')}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Карточки тарифов */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[16px]">
        {plans.map((plan) => {
          const isCurrentPlan = plan.id === tier;
          const isService = !!plan.isService;
          const isRecurringPlan = !!plan.isRecurring && showRecurringUi;
          const needsConsent = isRecurringPlan && !isCurrentPlan;
          const planConsentChecked = consentChecked[plan.id] === true;
          const buyDisabled =
            isLoading === plan.id || (needsConsent && !planConsentChecked);

          return (
            <div
              key={plan.id}
              className={`relative rounded-[16px] p-[24px] flex flex-col ${
                plan.popular
                  ? 'bg-[var(--accent-background,rgba(33,151,241,0.2))]'
                  : 'bg-[var(--white,#ffffff)] border border-[var(--grey-accent,#eeeeee)]'
              }`}
            >
              {/* Бейдж */}
              {plan.popular && (
                <div className="absolute top-[8px] right-[12px]">
                  <span className="inline-flex items-center justify-center px-[8px] py-[4px] rounded-[16px] bg-[var(--white,#ffffff)] font-manrope font-semibold text-[12px] leading-[16px] text-[color:var(--main-brand,#2197f1)]">
                    {t('badge.popular')}
                  </span>
                </div>
              )}

              {/* Заголовок с эмодзи */}
              <h3 className="font-manrope font-bold text-[24px] leading-[32px] mb-[24px] text-[color:var(--black-text,#0d0d0d)]">
                {t(plan.emojiKey)} {t(plan.nameKey)}
              </h3>

              {/* Цена */}
              <div className="flex items-center gap-[8px] mb-[24px]">
                <span className="font-manrope font-bold text-[32px] leading-[40px] text-[color:var(--black-text,#0d0d0d)]">
                  {plan.price === 0 ? '0' : plan.price.toLocaleString(lang === 'en' ? 'en-US' : 'ru-RU')}
                </span>
                <div className="flex flex-col justify-center">
                  <span className="font-segoe text-[12px] leading-[20px] text-[color:var(--black-text,#0d0d0d)]">
                    {t('price.currency')}
                    {isRecurringPlan && t('price.perMonth')}
                  </span>
                  {isService ? (
                    <span className="font-segoe text-[12px] leading-[20px] text-[color:var(--grey-text,#565858)]">
                      {t('price.oneTimeProject')}
                    </span>
                  ) : isRecurringPlan ? (
                    <span className="font-segoe text-[12px] leading-[20px] text-[color:var(--grey-text,#565858)]">
                      {t('price.monthlyBilling')}
                    </span>
                  ) : plan.id !== 'free' && (
                    <span className="font-segoe text-[12px] leading-[20px] text-[color:var(--grey-text,#565858)]">
                      {t('price.oneTimePayment')}
                    </span>
                  )}
                </div>
              </div>

              {/* Фичи */}
              <ul className="list-disc pl-[24px] space-y-[8px] mb-[24px]">
                {plan.featureKeys.map((featureKey) => (
                  <li key={featureKey} className="font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)]">
                    {t(featureKey)}
                  </li>
                ))}
                {plan.blockedFeatureKeys?.map((featureKey) => (
                  <li key={featureKey} className="font-segoe text-[16px] leading-[24px] text-[color:var(--grey-text,#565858)] line-through">
                    {t(featureKey)}
                  </li>
                ))}
              </ul>

              {/* Consent checkbox для basic/full */}
              {needsConsent && (
                <label className="mb-[12px] flex items-start gap-[8px] cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-[4px] h-[16px] w-[16px] shrink-0 accent-[var(--main-brand,#2197f1)] cursor-pointer"
                    checked={planConsentChecked}
                    onChange={(e) =>
                      setConsentChecked((prev) => ({ ...prev, [plan.id]: e.target.checked }))
                    }
                  />
                  <span className="font-segoe text-[12px] leading-[16px] text-[color:var(--grey-text,#565858)]">
                    {t('consent.recurring')}
                  </span>
                </label>
              )}

              {/* Кнопка */}
              <div className="mt-auto">
                {isService ? (
                  <button
                    onClick={() => setServiceModalOpen(true)}
                    className="block w-full h-[48px] rounded-[16px] bg-[var(--main-brand,#2197f1)] text-[color:var(--white,#ffffff)] font-segoe font-semibold text-[16px] leading-[48px] text-center hover:opacity-90 transition-opacity"
                  >
                    {t('buttons.discussProject')}
                  </button>
                ) : isCurrentPlan ? (
                  <div>
                    <Button
                      className="w-full h-[48px] rounded-[16px] bg-[var(--grey-accent,#eeeeee)] text-[color:var(--grey-text,#565858)] font-segoe font-semibold text-[14px] leading-[20px] cursor-not-allowed"
                      disabled
                    >
                      {t('buttons.currentPlan')}
                    </Button>
                    {isLegacyOneTime && endDate && (
                      <div className="mt-[8px] text-center font-segoe text-[14px] leading-[20px] text-[color:var(--grey-text,#565858)]">
                        <p>{t('buttons.daysRemaining', { count: daysRemaining })}</p>
                        <p className="text-[12px] opacity-70">
                          {t('buttons.expiresOn', {
                            date: new Date(endDate).toLocaleDateString(lang === 'en' ? 'en-US' : 'ru-RU'),
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                ) : plan.id === 'free' ? null : (
                  <Button
                    className="w-full h-[48px] rounded-[16px] bg-[var(--main-brand,#2197f1)] hover:bg-[var(--main-brand,#2197f1)] hover:opacity-90 text-[color:var(--white,#ffffff)] font-segoe font-semibold text-[16px] leading-[24px] disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={buyDisabled}
                    onClick={() => handleSubscribe(plan.id)}
                  >
                    {isLoading === plan.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      t('price.buyFor', { price: plan.price.toLocaleString(lang === 'en' ? 'en-US' : 'ru-RU') })
                    )}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Промокод */}
      <div className="mt-[32px]">
        <div className="flex gap-[12px] items-end">
          <div className="flex flex-col gap-[4px] w-full md:w-[364px]">
            <label className="font-segoe text-[14px] leading-[20px] text-[color:var(--grey-text,#565858)]">
              {t('promo.label')}
            </label>
            <input
              type="text"
              placeholder={t('promo.placeholder')}
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              className="w-full px-[16px] py-[12px] rounded-[16px] border border-[#eeeeee] bg-transparent font-segoe text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)] placeholder:text-[color:var(--placeholder,#969696)] focus:outline-none focus:border-[var(--main-brand,#2197f1)] transition-colors"
            />
          </div>
          <button
            className="h-[48px] px-[24px] py-[12px] rounded-[16px] bg-[var(--accent-background,rgba(33,151,241,0.2))] font-segoe font-semibold text-[16px] leading-[24px] text-[color:var(--main-brand,#2197f1)] hover:bg-[#2197f1] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleApplyPromoCode}
            disabled={promoLoading || !promoCode.trim()}
          >
            {promoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('promo.apply')}
          </button>
        </div>
        {promoError && (
          <p className="mt-[4px] font-segoe text-[12px] leading-[16px] text-[#dc2626]">{promoError}</p>
        )}
        {promoSuccess && (
          <p className="mt-[4px] font-segoe text-[12px] leading-[16px] text-[color:var(--main-brand,#2197f1)]">{promoSuccess}</p>
        )}
      </div>

      {/* Модалка смены тарифа */}
      <Dialog open={modalType === 'changePlan'} onOpenChange={() => setModalType(null)}>
        <DialogContent className="sm:max-w-[580px] p-[40px]">
          <DialogHeader>
            <DialogTitle className="font-manrope font-bold text-[32px] leading-[40px] text-[color:var(--black-text,#0d0d0d)]">{t('changePlanModal.title')}</DialogTitle>
            <DialogDescription className="font-segoe text-[16px] leading-[24px] text-[color:var(--grey-text,#565858)]">
              {t('changePlanModal.description')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-[16px] sm:gap-[16px]">
            <Button
              variant="outline"
              className="flex-1 h-[48px] rounded-[16px] border-[var(--grey-accent,#eeeeee)] font-segoe font-semibold text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)]"
              onClick={() => {
                setModalType(null);
                setPendingPlanId(null);
              }}
            >
              {t('changePlanModal.back')}
            </Button>
            <Button
              className="flex-1 h-[48px] rounded-[16px] bg-[var(--main-brand,#2197f1)] hover:bg-[var(--main-brand,#2197f1)] hover:opacity-90 font-segoe font-semibold text-[16px] leading-[24px] text-[color:var(--white,#ffffff)]"
              onClick={confirmChangePlan}
              disabled={isLoading !== null}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('changePlanModal.confirm')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Модалка подтверждения отмены подписки */}
      <Dialog
        open={modalType === 'cancelConfirm'}
        onOpenChange={(open) => {
          if (!open) {
            setModalType(null);
            setCancelError(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[580px] p-[40px]">
          <DialogHeader>
            <DialogTitle className="font-manrope font-bold text-[32px] leading-[40px] text-[color:var(--black-text,#0d0d0d)]">{t('cancel.confirm.title')}</DialogTitle>
            <DialogDescription className="font-segoe text-[16px] leading-[24px] text-[color:var(--grey-text,#565858)]">
              {t('cancel.confirm.description')}
            </DialogDescription>
          </DialogHeader>
          {cancelError && (
            <p className="font-segoe text-[14px] leading-[20px] text-[#dc2626]">{cancelError}</p>
          )}
          <DialogFooter className="flex gap-[16px] sm:gap-[16px]">
            <Button
              variant="outline"
              className="flex-1 h-[48px] rounded-[16px] border-[var(--grey-accent,#eeeeee)] font-segoe font-semibold text-[16px] leading-[24px] text-[color:var(--black-text,#0d0d0d)]"
              onClick={() => {
                setModalType(null);
                setCancelError(null);
              }}
              disabled={cancelLoading}
            >
              {t('cancel.confirm.back')}
            </Button>
            <Button
              className="flex-1 h-[48px] rounded-[16px] bg-[var(--main-brand,#2197f1)] hover:bg-[var(--main-brand,#2197f1)] hover:opacity-90 font-segoe font-semibold text-[16px] leading-[24px] text-[color:var(--white,#ffffff)]"
              onClick={handleCancelSubscription}
              disabled={cancelLoading}
            >
              {cancelLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('cancel.confirm.confirm')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Модалка успешной оплаты */}
      <Dialog open={modalType === 'success'} onOpenChange={() => setModalType(null)}>
        <DialogContent className="sm:max-w-[580px] p-[40px]">
          <DialogHeader>
            <DialogTitle className="font-manrope font-bold text-[32px] leading-[40px] text-[color:var(--black-text,#0d0d0d)]">{t('successModal.title')}</DialogTitle>
            <DialogDescription className="font-segoe text-[16px] leading-[24px] text-[color:var(--grey-text,#565858)]">
              {t('successModal.description')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              className="w-full h-[48px] rounded-[16px] bg-[var(--main-brand,#2197f1)] hover:bg-[var(--main-brand,#2197f1)] hover:opacity-90 font-segoe font-semibold text-[16px] leading-[24px] text-[color:var(--white,#ffffff)]"
              onClick={() => {
                setModalType(null);
                if (location.pathname !== '/subscription') {
                  window.location.href = '/subscription';
                } else {
                  window.history.replaceState({}, '', '/subscription');
                }
              }}
            >
              {t('successModal.button')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Модалка ошибки */}
      <Dialog open={modalType === 'error'} onOpenChange={() => setModalType(null)}>
        <DialogContent className="sm:max-w-[580px] p-[40px]">
          <DialogHeader>
            <DialogTitle className="font-manrope font-bold text-[32px] leading-[40px] text-[color:var(--black-text,#0d0d0d)]">{t('errorModal.title')}</DialogTitle>
            <DialogDescription className="font-segoe text-[16px] leading-[24px] text-[color:var(--grey-text,#565858)]">
              {t('errorModal.description')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              className="w-full h-[48px] rounded-[16px] bg-[var(--main-brand,#2197f1)] hover:bg-[var(--main-brand,#2197f1)] hover:opacity-90 font-segoe font-semibold text-[16px] leading-[24px] text-[color:var(--white,#ffffff)]"
              onClick={() => setModalType(null)}
            >
              {t('errorModal.button')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ServiceFormModal open={serviceModalOpen} onOpenChange={setServiceModalOpen} />
    </>
  );
}
